import { jwtDecode } from 'jwt-decode'

interface JWTPayload {
  sub: string
  scopes: string[]
  exp: number
}

export interface AccessToken {
  value: string
  expiresAt: Date
  permissions: string[]
}

export function parseAccessToken(jwt: string): AccessToken {
  try {
    const { exp, scopes } = jwtDecode<JWTPayload>(jwt)
    return {
      value: jwt,
      expiresAt: new Date(exp * 1000),
      permissions: scopes ?? [],
    }
  } catch {
    // Malformed JWT → treat as expired so the next getTokens() refreshes.
    return { value: jwt, expiresAt: new Date(0), permissions: [] }
  }
}

export const isExpired = (token: AccessToken): boolean =>
  new Date() >= token.expiresAt

const STORAGE_KEY = 'auth_tokens'

interface StoredTokens {
  accessToken: string
  refreshToken: string
}

export const tokenRepository = {
  save(accessToken: string, refreshToken: string): void {
    const payload: StoredTokens = { accessToken, refreshToken }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  },

  load(): { accessToken: AccessToken; refreshToken: string } | null {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    try {
      const { accessToken, refreshToken } = JSON.parse(raw) as StoredTokens
      if (!accessToken || !refreshToken) return null
      return { accessToken: parseAccessToken(accessToken), refreshToken }
    } catch {
      return null
    }
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEY)
  },
}
